import { Typography } from '@mui/material';
import { MapPointInput, type MapPoint } from '@re/form-ui';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import { useMapSupport } from '../api/map';
import type { PropertyDetails } from '../api/types';
import { getUiLanguage, tr } from '../lib/i18n';
import { SectionCard } from './org';
import { useToast } from './Toast';

export const pointOfRow = (p: { latitude: string | number | null; longitude: string | number | null }): MapPoint | null =>
  p.latitude != null && p.longitude != null && Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude))
    ? { lat: Number(p.latitude), lng: Number(p.longitude) }
    : null;

/**
 * «الموقع على الخريطة» on a property's page: the satellite map of the property, and for whoever may edit it
 * a link / my location / pick on map — saved straight away (properties added before the map existed get one here).
 */
export function PropertyMapCard({ p, onSaved, onConflict }: { p: PropertyDetails; onSaved: () => void; onConflict: (e: unknown) => void }) {
  const map = useMapSupport();
  const { toast } = useToast();
  const point = pointOfRow(p);
  const save = useMutation({
    mutationFn: (c: MapPoint | null) => api(`/properties/${p.id}`, { method: 'PATCH', body: { version: p.version, coordinates: c } }),
    onSuccess: (_, c) => {
      toast(c ? tr('تم حفظ موقع العقار', 'Location saved') : tr('تمت إزالة الموقع', 'Location removed'));
      onSaved();
    },
    onError: onConflict,
  });
  if (!point && !p.can.update) return null;
  return (
    <SectionCard title={tr('الموقع على الخريطة', 'Location on the map')}>
      {!point && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {tr('لم يُحدَّد موقع هذا العقار بعد. حدّده ليظهر على خريطة عقاراتك.', 'This property has no map location yet. Add one so it shows on your map.')}
        </Typography>
      )}
      <MapPointInput
        lang={getUiLanguage() === 'en' ? 'en' : 'ar'}
        value={point ?? undefined}
        map={map}
        disabled={!p.can.update || save.isPending}
        onChange={(v) => save.mutate(v ?? null)}
      />
    </SectionCard>
  );
}
